import { MapPin, RefreshCw } from "lucide-react";
import { colors, semantic } from "@/shared/design-system";
import { useGeolocationStore } from "@/shared/geo/geolocationStore";
import { AppModal, AppText, Spinner } from "@/shared/ui";

/**
 * Modal expliquant comment rouvrir l'accès à la position.
 *
 * ## Quand il apparaît — et quand il n'apparaît PAS
 * Uniquement sur une action qui a besoin de savoir où se trouve l'utilisateur :
 * lancer une course. Jamais à l'entrée de l'app.
 *
 * Un refus n'empêche pas de consulter ses commandes, son portefeuille ou son
 * profil : bloquer l'app entière punissait un choix légitime, et pour une
 * fonctionnalité que l'utilisateur n'était peut-être pas en train de demander.
 *
 * ## Deux discours, selon ce que le navigateur autorise encore
 * - **`prompt`** : l'invite native est encore possible → un bouton la déclenche.
 * - **`denied`** : le refus est MÉMORISÉ par le navigateur. `getCurrentPosition`
 *   échoue alors instantanément, sans rien afficher : un bouton « Autoriser »
 *   serait mort. Restent les réglages du site, d'où la marche à suivre.
 *
 * `permissions.onchange` referme le modal tout seul dès que l'autorisation
 * passe, sans que l'utilisateur ait à recharger.
 */
export function LocationPermissionDialog() {
  const isOpen = useGeolocationStore((s) => s.isPermissionDialogOpen);
  const close = useGeolocationStore((s) => s.closePermissionDialog);
  const permission = useGeolocationStore((s) => s.permission);
  const isRequesting = useGeolocationStore((s) => s.isRequesting);
  const request = useGeolocationStore((s) => s.request);

  const isDenied = permission === "denied";
  const isUnsupported = permission === "unsupported";

  return (
    <AppModal
      visible={isOpen}
      onClose={close}
      title="Activez votre localisation"
      subtitle={
        isUnsupported
          ? "Ce navigateur ne peut pas fournir votre position. Ouvrez Alomain depuis un navigateur récent, en HTTPS."
          : isDenied
            ? "L'accès à votre position a été refusé. Alomain en a besoin pour situer votre course, trouver la Main la plus proche et livrer au bon endroit."
            : "Alomain a besoin de votre position pour situer votre course et livrer au bon endroit."
      }
    >
      {/* `AppModal` ne pose de padding horizontal que sur son en-tête : le
          contenu, lui, arrive collé aux bords. C'est donc à l'appelant de le
          fournir — sans quoi le bouton touchait les deux côtés de la feuille. */}
      <div className="flex flex-col gap-4 px-5 pb-6 pt-1">
        <span
          aria-hidden
          className="mx-auto mt-1 flex h-14 w-14 items-center justify-center rounded-full bg-or-50"
        >
          <MapPin size={26} color={colors.or700} />
        </span>

        {isDenied && <ReactivationSteps />}

        {!isDenied && !isUnsupported && (
          <button
            type="button"
            onClick={() => void request().then((ok) => ok && close())}
            disabled={isRequesting}
            className="flex h-[52px] w-full items-center justify-center gap-2 rounded-btn bg-or-500 disabled:opacity-60"
          >
            {isRequesting && <Spinner size={18} color={colors.navy800} />}
            <AppText variant="button" color={semantic.contentOnBrand}>
              {isRequesting ? "Localisation en cours…" : "Autoriser la localisation"}
            </AppText>
          </button>
        )}

        {isDenied && (
          // `permissions.onchange` referme le modal tout seul là où il est
          // supporté ; ce bouton est le filet pour les navigateurs qui ne
          // l'implémentent pas.
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="flex h-[52px] w-full items-center justify-center gap-2 rounded-btn bg-gray-100"
          >
            <RefreshCw size={17} color={colors.navy800} aria-hidden />
            <AppText variant="button" color={colors.navy800}>
              J'ai autorisé — recharger
            </AppText>
          </button>
        )}

        {/* Sortie explicite : le refus reste un choix acceptable. Traitée comme
            un vrai bouton — même hauteur et même arrondi que l'action
            principale, en gris : en simple texte, elle ne se lisait pas comme
            cliquable. Le gris la garde clairement secondaire. */}
        <button
          type="button"
          onClick={close}
          className="flex h-[52px] w-full items-center justify-center rounded-btn bg-gray-100 transition-colors hover:bg-gray-200"
        >
          <AppText variant="button" color={colors.navy800}>
            Plus tard
          </AppText>
        </button>

        <AppText
          as="p"
          variant="tiny"
          color={colors.gray500}
          className="-mt-1 block text-balance text-center"
        >
          Votre position sert uniquement à vos courses. Elle n'est jamais
          partagée en dehors d'Alomain.
        </AppText>
      </div>
    </AppModal>
  );
}

/**
 * Marche à suivre pour rouvrir l'autorisation.
 *
 * Le libellé exact des menus varie d'un navigateur à l'autre ; le chemin, lui,
 * est constant — l'icône à gauche de l'adresse. On décrit ce repère plutôt
 * qu'une arborescence qui serait fausse une fois sur deux.
 */
function ReactivationSteps() {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  const steps = isIOS
    ? [
        "Touchez « AA » à gauche de l'adresse, puis « Réglages du site web ».",
        "Passez « Localisation » sur Autoriser.",
        "Revenez ici et relancez votre course.",
      ]
    : [
        "Touchez l'icône à gauche de l'adresse du site.",
        "Ouvrez les autorisations et passez « Localisation » sur Autoriser.",
        "Rechargez la page.",
      ];

  return (
    <div className="rounded-card bg-gray-50 p-3.5">
      <ol className="list-decimal space-y-1.5 pl-5">
        {steps.map((step) => (
          <li key={step}>
            <AppText variant="small" color={colors.gray500}>
              {step}
            </AppText>
          </li>
        ))}
      </ol>
    </div>
  );
}
